<template>
  <div class="d-flex flex-column gap-2">
    <div class="breakdown-bar d-flex rounded-2 overflow-hidden">
      <button
        v-for="segment in segments"
        :key="segment.key"
        type="button"
        class="breakdown-segment text-start text-nowrap overflow-hidden"
        :class="segment.isLight ? 'text-body-emphasis' : 'text-white'"
        :style="{
          flexGrow: segment.mwh,
          backgroundColor: segment.color,
        }"
        :aria-label="
          t('charts.panel.segment_label', {
            field: t(stackOption.labelKey, stackOption.label),
            value: segment.label,
            mwh: formatNumberWithSpaces(segment.mwh),
          })
        "
        @click="$emit('pick', segment.value)"
      >
        {{ inlineLabel(segment) }}
      </button>
    </div>
    <div
      class="d-flex align-items-center flex-wrap column-gap-4 row-gap-1 small text-body-tertiary"
    >
      <span
        v-for="segment in segments"
        :key="segment.key"
        class="d-inline-flex align-items-center gap-2"
      >
        <span
          class="legend-swatch rounded-1"
          :style="{ backgroundColor: segment.color }"
        ></span>
        <strong class="fw-semibold text-body-emphasis">{{
          segment.label
        }}</strong>
        {{
          t("charts.panel.mwh", { mwh: formatNumberWithSpaces(segment.mwh) })
        }}
      </span>
      <span class="ms-auto">{{ t("charts.panel.segment_hint") }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import {
  buildBreakdownSegments,
  type BreakdownOption,
  type BreakdownSegment,
} from "@/dashboard/inventory_management/overview/breakdownSegments";
import type { RawDataItem } from "@/types/ChartData";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  items: readonly RawDataItem[];
  stackOption: BreakdownOption;
  /** Item field holding the MWh each row adds to its segment. */
  yField: string;
}>();

defineEmits<{
  pick: [value: string | null];
}>();

const { t } = useI18n();

const segments = computed(() =>
  buildBreakdownSegments(props.items, props.stackOption, props.yField, t)
);

// Wide segments carry their name and amount, narrower ones the amount, the thinnest nothing.
const inlineLabel = (segment: BreakdownSegment): string => {
  const mwh = formatNumberWithSpaces(segment.mwh);
  if (segment.share >= 0.18) {
    return t("charts.panel.segment_inline", { label: segment.label, mwh });
  }
  return segment.share >= 0.09 ? mwh : "";
};
</script>

<style scoped lang="scss">
.breakdown-bar {
  gap: 2px;
  height: 30px;
}

.breakdown-segment {
  flex-basis: 0;
  min-width: 0;
  padding: 0 10px;
  border: 0;
  font-size: 0.75rem;
  font-weight: 600;

  &:hover {
    filter: brightness(1.08);
  }
}

.legend-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
}
</style>
